import api from "./api";

/**
 * Converts a URL-safe Base64 string to a Uint8Array for VAPID applicationServerKey
 */
export function urlBase64ToUint8Array(base64String) {
    const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

/**
 * Check if the browser environment supports Web Push notifications
 */
export function isPushSupported() {
    return (
        typeof window !== "undefined" &&
        "serviceWorker" in navigator &&
        "PushManager" in window &&
        "Notification" in window
    );
}

/**
 * Detect iOS device (iPhone / iPad / iPod)
 */
export function isIosDevice() {
    if (typeof window === "undefined") return false;
    const userAgent = window.navigator.userAgent || "";
    return /iPad|iPhone|iPod/.test(userAgent) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(userAgent));
}

/**
 * Check if current session is running in standalone PWA mode (added to home screen)
 */
export function isStandalonePwa() {
    if (typeof window === "undefined") return false;
    return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true ||
        document.referrer.includes("android-app://")
    );
}

/**
 * Get current browser notification permission
 */
export function getNotificationPermission() {
    if (typeof window === "undefined" || !("Notification" in window)) {
        return "unsupported";
    }
    return Notification.permission; // 'default' | 'granted' | 'denied'
}

/**
 * Subscribe the current device to push notifications
 */
export async function subscribeUserToPush() {
    if (!isPushSupported()) {
        throw new Error("Push notifications are not supported by this browser.");
    }

    // Request user permission if not already granted
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
        throw new Error(
            permission === "denied"
                ? "Notification permission was blocked in browser settings."
                : "Notification permission was dismissed."
        );
    }

    // 1. Get VAPID public key from backend
    const keyRes = await api.get("/notifications/vapid-public-key");
    const vapidPublicKey = keyRes.data?.public_key;
    if (!vapidPublicKey) {
        throw new Error("Could not retrieve VAPID public key from server.");
    }

    // 2. Wait for service worker ready
    const registration = await navigator.serviceWorker.ready;

    // 3. Subscribe or retrieve existing push subscription
    let subscription = await registration.pushManager.getSubscription();
    const convertedKey = urlBase64ToUint8Array(vapidPublicKey);

    if (subscription) {
        // Verify if existing subscription matches the current VAPID key
        let keyMatches = false;
        try {
            if (subscription.options && subscription.options.applicationServerKey) {
                const existingKey = new Uint8Array(subscription.options.applicationServerKey);
                if (existingKey.length === convertedKey.length) {
                    keyMatches = existingKey.every((val, idx) => val === convertedKey[idx]);
                }
            }
        } catch (err) {
            console.warn("Could not verify existing subscription key:", err);
        }

        if (!keyMatches) {
            console.log("Mismatched or stale VAPID key detected. Unsubscribing old subscription...");
            try {
                await subscription.unsubscribe();
            } catch (unsubErr) {
                console.warn("Error unsubscribing stale push key:", unsubErr);
            }
            subscription = null;
        }
    }

    if (!subscription) {
        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: convertedKey,
        });
    }

    // 4. Send subscription JSON to backend
    const subJson = subscription.toJSON();
    await api.post("/notifications/subscribe", {
        subscription: subJson,
        user_agent: navigator.userAgent,
        platform: isIosDevice() ? "iOS" : /Android/i.test(navigator.userAgent) ? "Android" : "Desktop",
    });

    return { success: true, subscription };
}

/**
 * Unsubscribe current device from push notifications
 */
export async function unsubscribeUserFromPush() {
    if (!isPushSupported()) return false;
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (subscription) {
            const endpoint = subscription.endpoint;
            await subscription.unsubscribe();
            await api.post("/notifications/unsubscribe", { endpoint });
        }
        return true;
    } catch (e) {
        console.error("Failed to unsubscribe:", e);
        return false;
    }
}

/**
 * Trigger a test push notification to verify receipt
 */
export async function sendTestPushNotification() {
    const res = await api.post("/notifications/test-push");
    return res.data;
}

/**
 * Fetch push subscription status for logged in user
 */
export async function checkPushStatus() {
    try {
        const res = await api.get("/notifications/status");
        return res.data;
    } catch {
        return { subscribed: false, subscription_count: 0 };
    }
}

/**
 * Sync active browser subscription with backend for the currently logged-in user
 */
export async function syncSubscriptionWithBackend() {
    if (!isPushSupported()) return false;
    try {
        if (typeof Notification === "undefined" || Notification.permission !== "granted") return false;
        const registration = await navigator.serviceWorker.ready;
        let subscription = await registration.pushManager.getSubscription();
        if (!subscription) {
            // Try to auto-subscribe if already granted
            const keyRes = await api.get("/notifications/vapid-public-key");
            const vapidPublicKey = keyRes.data?.public_key;
            if (!vapidPublicKey) return false;
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
            });
        }
        if (!subscription) return false;

        const subJson = subscription.toJSON();
        await api.post("/notifications/subscribe", {
            subscription: subJson,
            user_agent: navigator.userAgent,
            platform: isIosDevice() ? "iOS" : /Android/i.test(navigator.userAgent) ? "Android" : "Desktop",
        });
        return true;
    } catch (e) {
        console.warn("Could not sync subscription with backend:", e);
        return false;
    }
}

/**
 * Check if the current browser/device has an active push subscription matching current server VAPID key
 */
export async function isCurrentDeviceSubscribed() {
    if (!isPushSupported()) return false;
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();
        if (!subscription) return false;

        const keyRes = await api.get("/notifications/vapid-public-key");
        const vapidPublicKey = keyRes.data?.public_key;
        if (!vapidPublicKey) return false;

        const convertedKey = urlBase64ToUint8Array(vapidPublicKey);
        if (subscription.options && subscription.options.applicationServerKey) {
            const existingKey = new Uint8Array(subscription.options.applicationServerKey);
            if (existingKey.length === convertedKey.length) {
                return existingKey.every((val, idx) => val === convertedKey[idx]);
            }
        }
        return false;
    } catch {
        return false;
    }
}
